import { useState } from 'react';
import { BarChart, Gauge } from '../components/charts';
import { IconChart, IconDownload } from '../components/icons';
import { Badge, Card, EmptyState, Loading, ModuleHeader, Tabs, useAsync, useToast } from '../components/ui';
import { api, downloadFile } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCurrency, formatDuration } from '../lib/format';

export function ReportsPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [tab, setTab] = useState<'sla' | 'kosten' | 'auffaellig'>('sla');
  const [range, setRange] = useState({ from: '', to: '' });

  const sla = useAsync(() => (can('reports.operational') ? api.get('/api/reports/sla', range) : Promise.resolve(null)), [
    JSON.stringify(range),
  ]);
  const costs = useAsync(() => (can('reports.financial') ? api.get('/api/reports/costs', range) : Promise.resolve(null)), [
    JSON.stringify(range),
  ]);
  const recurring = useAsync(
    () => (can('reports.operational') ? api.get('/api/reports/recurring') : Promise.resolve([])),
    [],
  );

  const availableTabs = [
    ...(can('reports.operational') ? [{ key: 'sla' as const, label: 'SLA & Aufkommen' }] : []),
    ...(can('reports.financial') ? [{ key: 'kosten' as const, label: 'Kosten je Objekt' }] : []),
    ...(can('reports.operational') ? [{ key: 'auffaellig' as const, label: 'Auffällige Objekte' }] : []),
  ];

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconChart width={22} height={22} />}
        title="Auswertungen"
        subtitle={
          <>
            Kennzahlen zu SLA-Erfüllung, Aufkommen je Kanal und Kategorie sowie Kosten je Objekt – als Nachweis
            gegenüber Eigentümern/Verwaltern und Grundlage für die Weiterberechnung.
          </>
        }
        pill="Auswertung"
        tone="accent"
        actions={
          <>
            <input type="date" value={range.from} onChange={(event) => setRange({ ...range, from: event.target.value })} />
            <input type="date" value={range.to} onChange={(event) => setRange({ ...range, to: event.target.value })} />
            {can('reports.export') && (
              <button
                className="btn"
                onClick={() =>
                  downloadFile('/api/reports/export.csv', 'auswertung.csv').catch(() => notify('Export fehlgeschlagen.', 'error'))
                }
              >
                <IconDownload width={15} height={15} /> Export
              </button>
            )}
          </>
        }
      />

      {availableTabs.length === 0 ? (
        <EmptyState title="Keine Auswertungsrechte" hint="Für diesen Bereich sind zusätzliche Berechtigungen erforderlich." />
      ) : (
        <>
          <Tabs active={tab} onChange={setTab} tabs={availableTabs} />

          {tab === 'sla' &&
            (sla.loading ? (
              <Loading />
            ) : !sla.data ? (
              <EmptyState title="Keine Daten" />
            ) : (
              <div className="stack">
                <div className="grid cols-2">
                  <Card title="SLA-Erfüllung je Kritikalität" subtitle="Vereinbarte gegen tatsächliche Erledigungszeit">
                    <div className="stack">
                      {(sla.data.byCriticality ?? []).map((entry: any) => {
                        const quota = entry.total > 0 ? ((entry.met ?? 0) / entry.total) * 100 : 0;
                        return (
                          <div key={entry.id ?? entry.name} className="stack" style={{ gap: 4 }}>
                            <Gauge value={quota} label={`${entry.name} (${entry.total} Vorgänge)`} />
                            <div className="small subtle">
                              Ø Erledigung {formatDuration(entry.avg_resolution_minutes)} · vereinbart{' '}
                              {formatDuration(entry.resolution_minutes)}
                            </div>
                          </div>
                        );
                      })}
                      {(sla.data.byCriticality ?? []).length === 0 && <EmptyState title="Keine abgeschlossenen Vorgänge" />}
                    </div>
                  </Card>

                  <Card title="Aufkommen je Meldekanal" subtitle="Woher kommen die Meldungen?">
                    {(sla.data.byChannel ?? []).length ? (
                      <BarChart
                        data={sla.data.byChannel.map((entry: any) => ({
                          id: entry.id,
                          label: entry.name,
                          value: entry.total,
                        }))}
                      />
                    ) : (
                      <EmptyState title="Keine Daten" />
                    )}
                  </Card>
                </div>

                <div className="grid cols-2">
                  <Card title="Aufkommen je Kategorie / Gewerk">
                    {(sla.data.byCategory ?? []).length ? (
                      <BarChart
                        data={sla.data.byCategory.map((entry: any) => ({
                          id: entry.id,
                          label: entry.name,
                          value: entry.total,
                          color: entry.color,
                        }))}
                      />
                    ) : (
                      <EmptyState title="Keine Daten" />
                    )}
                  </Card>

                  <Card title="SLA je Objekt" subtitle="Erfüllte und verletzte Fristen" noBody>
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr>
                            <th>Objekt</th>
                            <th>Eigentümer/Verwalter</th>
                            <th className="num">Vorgänge</th>
                            <th className="num">Erfüllt</th>
                            <th className="num">Verletzt</th>
                            <th className="num">Ø Reaktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(sla.data.byProperty ?? []).map((entry: any) => (
                            <tr key={entry.id}>
                              <td>{entry.name}</td>
                              <td className="small subtle">{entry.customer_name ?? '–'}</td>
                              <td className="num">{entry.total}</td>
                              <td className="num">{entry.met ?? 0}</td>
                              <td className="num">
                                {entry.breached > 0 ? <Badge variant="danger">{entry.breached}</Badge> : 0}
                              </td>
                              <td className="num small">{formatDuration(entry.avg_response_minutes)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              </div>
            ))}

          {tab === 'kosten' &&
            (costs.loading ? (
              <Loading />
            ) : !costs.data?.items?.length ? (
              <EmptyState title="Keine Kostendaten im Zeitraum" />
            ) : (
              <Card
                title="Kosten je Objekt"
                subtitle={`Personalkosten kalkuliert mit ${formatCurrency(costs.data.hourlyRate)} pro Stunde`}
                noBody
              >
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Objekt</th>
                        <th>Eigentümer/Verwalter</th>
                        <th className="num">Vorgänge</th>
                        <th className="num">Arbeitszeit</th>
                        <th className="num">Personalkosten</th>
                        <th className="num">Materialkosten</th>
                        <th className="num">Gesamt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costs.data.items.map((entry: any) => (
                        <tr key={entry.id}>
                          <td>{entry.name}</td>
                          <td className="small subtle">{entry.customer_name ?? '–'}</td>
                          <td className="num">{entry.task_count}</td>
                          <td className="num">{formatDuration(entry.labor_minutes)}</td>
                          <td className="num">{formatCurrency(entry.labor_cost)}</td>
                          <td className="num">{formatCurrency(entry.material_cost)}</td>
                          <td className="num">
                            <strong>{formatCurrency(entry.total_cost)}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}

          {tab === 'auffaellig' &&
            (recurring.loading ? (
              <Loading />
            ) : !recurring.data?.length ? (
              <EmptyState
                title="Keine Häufungen erkannt"
                hint="Ausgewertet werden Objekte mit mindestens drei gleichartigen Vorgängen in 180 Tagen."
              />
            ) : (
              <Card
                title="Wiederkehrende Störungen"
                subtitle="Hinweis auf strukturelle Mängel – mindestens drei gleichartige Vorgänge in 180 Tagen"
                noBody
              >
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Objekt</th>
                        <th>Kategorie</th>
                        <th className="num">Vorgänge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recurring.data.map((entry: any, index: number) => (
                        <tr key={index}>
                          <td>{entry.name}</td>
                          <td>{entry.category_name ?? '–'}</td>
                          <td className="num">
                            <Badge variant={entry.count >= 5 ? 'danger' : 'warning'}>{entry.count}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))}
        </>
      )}
    </div>
  );
}
